import React, { useCallback, useEffect, useRef, useState } from 'react';
import CheckIcon from 'public/icons/ag-icons/check.svg';
import Loading from 'components/commercetools-ui/atoms/loading';
import useScrollBlock from 'helpers/hooks/useScrollBlock';
import useOnClickOutside from 'helpers/hooks/useOnClickOutside';
import Modal from 'components/commercetools-ui/atoms/modal';
import { XMarkIcon } from '@heroicons/react/24/solid';
import { customFitAttributeDetails, CustomFitMapping } from '../types';
import productJson from 'public/locales/en/product.json';
import Select, { default as ReactSelect, StylesConfig, components } from 'react-select';
import { OptionProps } from 'react-select';
import { useFormat } from 'helpers/hooks/useFormat';

const getHasValue = (state: any): boolean => {
  const { options } = state || {};
  return Array.isArray(options) ? options?.length > 0 : Boolean(options);
};

const customStyles: StylesConfig = {
  control: (base, state) => {
    const hasValue = getHasValue(state);
    return {
      ...base,
      borderColor: hasValue ? '#767676' : '#686868',
      '&:hover': {
        borderColor: hasValue ? '#767676' : '',
      },
      zIndex: 1,
    };
  },
  placeholder: (base, state) => {
    const hasValue = getHasValue(state);
    return {
      ...base,
      color: hasValue ? '#1e1e1e' : '#686868',
      fontSize: '14px',
      fontWeight: '500',
      fontFamily: 'sans-serif',
      lineHeight: 1.15,
    };
  },
  input: (base) => ({
    ...base,
    fontSize: '16px', // Set to 16px to avoid iOS zoom
    transform: 'scale(0.875)', // Scales 16px to approx 14px visually
    transformOrigin: 'top left', // Ensures proper alignment
  }),
  menu: (base) => ({
    ...base,
    zIndex: 9999,
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999,
  }),
};

const { customFitAttributename } = productJson;
const attributeNameMapping: CustomFitMapping['customFitAttributename'] = customFitAttributename;

const dependancy: Record<string, string[]> = {
  Dexterity: ['Sex'],
  Loft: ['Sex', 'Dexterity'],
  Grind: ['Sex', 'Dexterity', 'Loft'],
  Bounce: ['Sex', 'Dexterity', 'Loft', 'Grind'],
  Neck: ['Sex', 'Dexterity'],
  Flex: ['Shaft'], // Flex depends on Shaft
};

const labelMapping = {
  Sex: 'Gender',
  IronSets: 'Set Make-Up',
  LoftAdj: 'Loft Adjustment',
  LieAdj: 'Lie Adjustment',
  LengthAdj: 'Length Adjustment',
  GripThickness: 'Grip Thickness',
};

const Option: React.FC<OptionProps<unknown, boolean>> = (props) => {
  return (
    <div>
      <components.Option {...props}>
        <input
          type="checkbox"
          checked={props.isSelected}
          onChange={() => null} // Prevent React warnings for uncontrolled input
        />{' '}
        <label>{props.label}</label>
      </components.Option>
    </div>
  );
};

// New function to calculate ETA based on configDetails with fallback
const calculateETADays = (
  selectedAttributes: Record<string, any>,
  configDetails: any[],
  fallbackEtaDays: number,
): number => {
  // If no configDetails available, use fallback
  if (!configDetails || configDetails?.length === 0) {
    return fallbackEtaDays;
  }

  // Transform selected attributes to match the format needed for comparison
  const transformed: any = {};

  // Map the selected attributes using labelMappingRev if needed
  Object.keys(selectedAttributes)?.forEach((key) => {
    const value = selectedAttributes?.[key];
    if (value !== null && value !== undefined && value !== '') {
      // Handle array values (like IronSets multi-select)
      if (Array.isArray(value)) {
        transformed[key] = value?.length > 0 ? value?.[0] : null; // Use first value for matching
      } else {
        transformed[key] = value;
      }
    }
  });

  // Find matching configuration
  let result = null; // Use null to distinguish between found 0 and no match
  for (const configObj of configDetails) {
    let isMatch = true;

    // Check each property in the config object
    for (const key in configObj) {
      const configValue = configObj?.[key];

      // Skip etaDays field and null values in config
      if (key === 'etaDays' || key === 'Type' || configValue === null) {
        continue;
      }

      // Get the corresponding value from transformed attributes
      const selectedValue = transformed?.[key];

      // If config has a non-null value, it must match the selected value
      if (String(selectedValue)?.trim() !== String(configValue)?.trim()) {
        isMatch = false;
        break;
      }
    }

    if (isMatch) {
      result = configObj?.etaDays || 0;
      break;
    }
  }
  result += fallbackEtaDays;

  return result;
};

const CustomFitNew: React.FC<any> = ({
  product,
  variant,
  scrollable,
  setIsCartDisable,
  setCustomFitPrice,
  customFitPrice,
  setPayloadAttributes,
  setSelectedSKUs,
  setQuantity,
  currency,
  selectedSKUs,
  setDiscountedMRPrices,
  baseMPRPrice,
  setDeliveryDate,
  deliveryDate,
  showSizeGuide,
}) => {
  const [selectedAttributes, setSelectedAttributes] = useState<Record<string, any>>({});
  const [dropdownOptions, setDropdownOptions] = useState<Record<string, any[]>>({});
  const [loadingState, setLoadingState] = useState<Record<string, boolean>>({});
  const [attributesState, setAttributesState] = useState<Record<string, any>>({});
  const [selectedOptionPrices, setSelectedOptionPrices] = useState<{
    Shaft?: number;
    Grip?: number;
    IronSets?: number;
    ironSetsCount: number;
  }>({
    ironSetsCount: 1, // Default multiplier for IronSets
    Shaft: 0, // Default Shaft price
    Grip: 0, // Default Grip price
    IronSets: 0, // Default IronSets price
  });
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const [modalContent, setModalContent] = useState<string | null>(null);
  const [modalIsOpen, setIsOpen] = useState(false);
  const [mappedAttribute, setMappedAttribute] = useState<string | null>(null);
  const customFitAttributes = product?.customFitAttributeDetails as customFitAttributeDetails[];
  const { blockScroll } = useScrollBlock();
  const ref = useRef<HTMLDivElement>(null);
  // const [deliveryDate, setDeliveryDate] = useState<string>(''); // State to store the delivery date

  // Function to open the modal with dynamic content
  const openModal = (content: string, attributeName: string) => {
    setModalContent(content);
    setMappedAttribute(attributeName);
    setIsOpen(true);
    blockScroll(true);
  };
  // Function to close the modal
  const closeModal = () => {
    setIsOpen(false);
    setModalContent(null);
    blockScroll(false);
  };

  // Function to get modal content based on attribute name
  const getModalContent = (attributeName: string) => {
    const normalizedAttributeName = `${attributeName?.charAt(0)?.toLowerCase()}${attributeName?.slice(1)}`;
    const matchedAttribute = customFitAttributes?.find((attr: any) => attr?.name === normalizedAttributeName);

    if (matchedAttribute) {
      const description = matchedAttribute?.inputTip?.['en-GB'];
      return description || 'No description available.';
    } else {
      return 'No matching attribute found.';
    }
  };

  useOnClickOutside(ref, () => closeModal());

  const isAttributePresentMap = customFitAttributes?.reduce(
    (acc: Record<string, boolean>, attr: customFitAttributeDetails) => {
      acc[attr.name] = true;
      return acc;
    },
    {} as Record<string, boolean>,
  );

  const customFitDetails: any = product?.customFitDetails || [];
  const categories: any[] = customFitDetails?.[0]?.productJSON?.categories || [];
  const configDetails: any[] = customFitDetails?.[0]?.productJSON?.configDetails || [];

  // Initialize attributesState with all attribute names
  useEffect(() => {
    const initialAttributesState: Record<string, any> = {};
    categories?.forEach((category) => {
      initialAttributesState[category?.name] = null; // Initialize with null or default value
    });
    setAttributesState(initialAttributesState);
  }, []);

  const buildDependencyMap = useCallback(() => {
    const dependencyMap: Record<string, string[]> = {};
    const sortedCategories = [...categories]?.sort((a, b) => a?.sequenceNumber - b?.sequenceNumber);

    for (let i = 1; i < sortedCategories?.length; i++) {
      const current = sortedCategories[i];
      // Assume previous categories are dependencies
      dependencyMap[current?.name] = sortedCategories?.slice(0, i)?.map((cat) => cat?.name);
    }

    return dependencyMap;
  }, [categories]);

  const dependencyMap = buildDependencyMap();

  // Check if an attribute's dependencies are satisfied
  const isAttributeEnabled = useCallback(
    (attributeName: string): boolean => {
      const dependencies = dependencyMap?.[attributeName];
      if (!dependencies || dependencies?.length === 0) return true;

      return dependencies?.every(
        (dep) => selectedAttributes?.[dep] !== undefined && selectedAttributes?.[dep] !== null,
      );
    },
    [dependencyMap, selectedAttributes],
  );

  const fetchOptions = useCallback(
    (attributeName: string): void => {
      if (!isAttributeEnabled(attributeName)) {
        setDropdownOptions((prev) => ({ ...prev, [attributeName]: [] }));
        return;
      }
      setLoadingState((prev) => ({ ...prev, [attributeName]: true }));

      let options: any[] = [];
      if (attributeName === 'Flex') {
        // Special case for Flex: Fetch options from inside the Shaft attribute
        const selectedShaft = selectedAttributes?.['Shaft'];
        const shaftOptions = customFitDetails?.[0]?.Shaft || [];
        const matchingShaft = shaftOptions?.find((shaft: any) => shaft?.value === selectedShaft);

        if (matchingShaft?.flex) {
          options = matchingShaft?.flex?.map((flexOption: any) => ({
            label: flexOption?.label,
            value: flexOption?.value,
          }));
        }
      } else if (['Grind', 'Bounce', 'Neck', 'Loft']?.includes(attributeName)) {
        // Get all parent attributes this option depends on
        const parentAttributes = dependancy?.[attributeName] || [];

        // Get the selected values for all parent attributes
        const parentValues = parentAttributes?.map((attr) => selectedAttributes?.[attr]);

        // Filter options based on parent values
        options = (customFitDetails?.[0]?.options || [])
          .filter((option: any) => {
            return parentValues?.every((value, index) => {
              const parentAttr = parentAttributes?.[index];

              // Special handling for nested objects like Grind, Bounce, Loft
              if (option[parentAttr]?.value) {
                return option?.[parentAttr]?.value === value;
              }
              return option?.[parentAttr] === value;
            });
          })
          .map((option: any) => {
            // Handle nested attribute objects
            const attrValue = option?.[attributeName];

            return {
              label: attrValue?.label || option[`label_${attributeName}`] || attributeName,
              value: attrValue?.value || option[attributeName],
              price: attrValue?.price || 0,
              sku: attrValue?.sku || '',
            };
          });
      } else if (customFitDetails?.[0]?.[attributeName]) {
        // If the attribute is present as a key outside of options, fetch it directly
        options = (customFitDetails?.[0]?.[attributeName] as any[]) || [];
      } else {
        // Use dependency logic to filter options
        const parentValues = dependancy?.[attributeName]?.map((dep) => selectedAttributes?.[dep]) || [];
        options =
          (customFitDetails?.[0]?.options as any[])
            ?.filter((option) =>
              parentValues?.every((value, index) => {
                if (!value) return false; // Ensure parent value is selected
                return option?.[dependancy?.[attributeName]?.[index]] === value;
              }),
            )
            ?.map((option) => ({
              label: option?.[`label_${attributeName}`],
              value: option?.[attributeName],
            })) || [];
      }

      const distinctOptions = Array.from(new Set(options?.map((opt) => JSON.stringify(opt))))?.map((opt) =>
        JSON.parse(opt),
      );

      setDropdownOptions((prev) => ({ ...prev, [attributeName]: distinctOptions }));
      setLoadingState((prev) => ({ ...prev, [attributeName]: false }));
    },
    [isAttributeEnabled, selectedAttributes, dependencyMap],
  );

  const handleSelectionChange = (attributeName: string, value: any): void => {
    if (!value) {
      console.error(`Invalid value for attribute: ${attributeName}`);
      return;
    }

    // Store previous state for comparison
    const previousState = {
      prices: { ...selectedOptionPrices },
      attributes: { ...attributesState },
      ironSetsCount: selectedOptionPrices.ironSetsCount,
      skus: { ...selectedSKUs },
    };

    // Determine if this is a parent attribute change
    const isParentAttributeChange = Object.keys(dependencyMap)?.some((depAttr) =>
      dependencyMap?.[depAttr]?.includes(attributeName),
    );

    // Prepare new state
    const newState = {
      attributes: {
        ...attributesState,
        [attributeName]: Array.isArray(value) ? value?.map((v) => v?.value) : value,
      },
      prices: { ...selectedOptionPrices },
      skus: { ...selectedSKUs },
    };

    // Clear all dependent attributes when a parent changes
    if (isParentAttributeChange) {
      Object.keys(dependencyMap)?.forEach((depAttr) => {
        if (dependencyMap?.[depAttr]?.includes(attributeName)) {
          // Clear the dependent attribute
          delete newState?.attributes?.[depAttr];

          // Reset dropdown options for dependent attribute
          setDropdownOptions((prev) => ({ ...prev, [depAttr]: [] }));

          // Reset prices and SKUs for dependent attributes
          if (['Shaft', 'Grip', 'IronSets']?.includes(depAttr)) {
            (newState?.prices as any)[depAttr] = 0;
            delete newState?.skus?.[depAttr];
          }

          // Special handling for IronSets
          if (depAttr === 'IronSets') {
            newState.prices.ironSetsCount = 1;
          }
        }
      });
    }

    // Calculate new iron sets count - FIX: Handle empty/cleared IronSets properly
    let newIronSetsCount = 1; // Default count

    if (attributeName === 'IronSets') {
      // If IronSets is being set/changed
      if (Array.isArray(value) && value.length > 0) {
        newIronSetsCount = value.length;
      } else if (value && !Array.isArray(value)) {
        newIronSetsCount = 1;
      } else {
        // IronSets is being cleared/removed
        newIronSetsCount = 1;
        newState.attributes.IronSets = null; // Explicitly set to null when cleared
      }
    } else if (newState?.attributes?.IronSets) {
      // For other attributes, check existing IronSets
      if (Array.isArray(newState.attributes.IronSets)) {
        newIronSetsCount = newState.attributes.IronSets.length;
      } else {
        newIronSetsCount = 1;
      }
    }

    // Update iron sets count in prices
    newState.prices.ironSetsCount = newIronSetsCount;

    // Calculate new price from scratch and handle SKUs
    const calculateNewPriceAndSKUs = () => {
      const baseCustomFitPrice = product?.customFitDetails?.[0]?.productJSON?.CF_Price?.[currency] || 0;

      // FIX: Only multiply by count if IronSets is actually selected
      const shouldMultiplyByCount =
        newState?.attributes?.IronSets &&
        ((Array.isArray(newState.attributes.IronSets) && newState.attributes.IronSets.length > 0) ||
          (!Array.isArray(newState.attributes.IronSets) && newState.attributes.IronSets !== null));

      let totalPrice = shouldMultiplyByCount ? baseCustomFitPrice * newIronSetsCount : baseCustomFitPrice;
      let totalMRprice = shouldMultiplyByCount ? baseMPRPrice * newIronSetsCount : baseMPRPrice;

      // Handle Shaft selection and SKU
      if (attributeName === 'Shaft') {
        const shaftOption = dropdownOptions?.['Shaft']?.find((opt) => opt?.value === value);
        if (shaftOption) {
          const shaftPrice = shouldMultiplyByCount ? shaftOption?.price * newIronSetsCount : shaftOption?.price;
          newState.prices.Shaft = shaftPrice;
          totalPrice += shaftPrice;
          totalMRprice += shaftPrice;
          newState.skus.Shaft = shaftOption?.sku;
        } else {
          newState.prices.Shaft = 0;
          delete newState?.skus?.Shaft;
        }
      }
      // Handle existing Shaft when other attributes change
      else if (newState?.attributes?.Shaft) {
        const shaftOption = dropdownOptions?.['Shaft']?.find((opt) => opt?.value === newState?.attributes?.Shaft);
        if (shaftOption) {
          const shaftPrice = shouldMultiplyByCount ? shaftOption?.price * newIronSetsCount : shaftOption?.price;
          newState.prices.Shaft = shaftPrice;
          totalPrice += shaftPrice;
          totalMRprice += shaftPrice;
          newState.skus.Shaft = shaftOption?.sku;
        }
      }

      // Handle Grip selection and SKU
      if (attributeName === 'Grip') {
        const gripOption = dropdownOptions?.['Grip']?.find((opt) => opt?.value === value);
        if (gripOption) {
          const gripPrice = shouldMultiplyByCount ? gripOption?.price * newIronSetsCount : gripOption?.price;
          newState.prices.Grip = gripPrice;
          totalPrice += gripPrice;
          totalMRprice += gripPrice;

          newState.skus.Grip = gripOption?.sku;
        } else {
          newState.prices.Grip = 0;
          delete newState?.skus?.Grip;
        }
      }
      // Handle existing Grip when other attributes change
      else if (newState?.attributes?.Grip) {
        const gripOption = dropdownOptions?.['Grip']?.find((opt) => opt?.value === newState?.attributes?.Grip);
        if (gripOption) {
          const gripPrice = shouldMultiplyByCount ? gripOption?.price * newIronSetsCount : gripOption?.price;
          newState.prices.Grip = gripPrice;
          totalPrice += gripPrice;
          totalMRprice += gripPrice;
          newState.skus.Grip = gripOption?.sku;
        }
      }

      return { totalPrice, totalMRprice };
    };

    const newPrice = calculateNewPriceAndSKUs();

    // Update all states atomically
    setSelectedAttributes(newState?.attributes);
    setAttributesState(newState?.attributes);
    setSelectedOptionPrices(newState?.prices);
    setSelectedSKUs(newState?.skus);
    setCustomFitPrice(newPrice?.totalPrice);

    // FIX: Set quantity based on whether IronSets is selected or not
    const finalQuantity =
      newState?.attributes?.IronSets &&
      ((Array.isArray(newState.attributes.IronSets) && newState.attributes.IronSets.length > 0) ||
        (!Array.isArray(newState.attributes.IronSets) && newState.attributes.IronSets !== null))
        ? newIronSetsCount
        : 1;

    setQuantity(finalQuantity);
    checkIfAllSelected();
    setDiscountedMRPrices(newPrice?.totalMRprice);
  };

  useEffect(() => {
    checkIfAllSelected();
  }, [attributesState, categories]); // Add dependencies

  // Updated effect to calculate ETA days and delivery date dynamically
  useEffect(() => {
    // Get fallback ETA days from productJSON
    const fallbackEtaDays = customFitDetails?.[0]?.productJSON?.ETADays || 0;

    // Calculate ETA days based on current selections with fallback
    const calculatedEtaDays = calculateETADays(selectedAttributes, configDetails, fallbackEtaDays);
    // Calculate delivery date using the dynamic ETA
    const calculatedDate = calculateDeliveryDate(calculatedEtaDays);
    setDeliveryDate(calculatedDate);
  }, [selectedAttributes, configDetails, customFitDetails]);

  // Modify the checkIfAllSelected function to not depend on closure values
  const checkIfAllSelected = useCallback(() => {
    const requiredAttributes = categories
      ?.sort((a, b) => a?.sequenceNumber - b?.sequenceNumber)
      ?.filter((category) => !category?.dependOn || selectedAttributes?.[category?.dependOn]);

    const allSelected = requiredAttributes?.every((category) => {
      if (category?.name === 'IronSets') {
        return selectedAttributes?.['IronSets'] && selectedAttributes?.['IronSets']?.length > 0;
      }
      return (
        selectedAttributes?.[category?.name] !== undefined &&
        selectedAttributes?.[category?.name] !== null &&
        selectedAttributes?.[category?.name] !== ''
      );
    });

    setIsCartDisable(!allSelected);
    return allSelected;
  }, [selectedAttributes, categories, setIsCartDisable]);

  useEffect(() => {
    categories?.forEach((category) => {
      const attributeName = category?.name;

      if (!dependancy?.[attributeName] || dependancy?.[attributeName]?.every((dep) => selectedAttributes?.[dep])) {
        fetchOptions(attributeName);
      }
    });
  }, [selectedAttributes]);

  useEffect(() => {
    if (Object.keys(attributesState)?.length > 0 && Object.keys(dropdownOptions)?.length > 0) {
      const mappedAttributes = Object.keys(attributesState)?.map((attrName) => {
        const options = dropdownOptions?.[attrName];

        if (!options) return { name: attrName, value: attributesState?.[attrName] };

        if (Array.isArray(attributesState?.[attrName])) {
          const labels = attributesState?.[attrName]
            .map((val: any) => options?.find((option) => option?.value === val)?.label || val)
            .filter(Boolean);

          return { name: attrName, value: labels };
        } else {
          const matchingOption = options?.find((option) => option?.value == attributesState?.[attrName]);
          return {
            name: attrName,
            value: matchingOption ? matchingOption?.label : attributesState?.[attrName],
          };
        }
      });

      setPayloadAttributes(mappedAttributes);
    }
  }, [attributesState, dropdownOptions]);

  return (
    <div className="relative">
      <div className="pb-16 text-start">
        {categories &&
          categories?.length > 0 &&
          categories
            ?.sort((a, b) => a?.sequenceNumber - b?.sequenceNumber) // Sort by sequenceNumber
            ?.map((category, index) => {
              const enabled = isAttributeEnabled(category?.name);
              const normalizedAttributeName = `${category?.name?.[0]?.toLowerCase()}${category?.name?.slice(1)}`;
              const mappedAttributeName = attributeNameMapping?.[normalizedAttributeName] || normalizedAttributeName;
              return (
                <div key={index} className="flex flex-col items-start pb-16 text-start">
                  <div className="mb-4 flex w-full items-center justify-between gap-x-3 pr-32">
                    <div className="flex items-center gap-x-3">
                      <p className="text-14 font-normal">{(labelMapping as any)?.[category?.name] || category?.name}</p>
                      {customFitAttributes?.length > 0 && isAttributePresentMap?.[mappedAttributeName] && (
                        <span
                          className="ml-2 cursor-pointer"
                          onClick={() => openModal(getModalContent(category?.name), category?.name)}
                        >
                          <div className="flex h-16 w-16 items-center justify-center rounded-full border bg-[#686868] text-12 text-white">
                            ?
                          </div>
                        </span>
                      )}
                    </div>
                    {index === 0 && product?.sizeGuide !== null && product?.sizeguide?.tableSet?.length > 0 && (
                      <div>
                        <p className="text-right">
                          <span
                            className="link-arrow dialoglink cursor-pointer text-12 font-normal capitalize underline sm:text-14"
                            onClick={showSizeGuide}
                          >
                            <i>{formatProductMessage({ id: 'clubsSizeGuide', defaultMessage: 'Find Your Fit' })}</i>
                          </span>
                        </p>
                      </div>
                    )}
                  </div>

                  <div className="flex w-full items-center gap-x-12">
                    {loadingState?.[category?.name] ? (
                      <Loading />
                    ) : category?.multiSelect ? (
                      <ReactSelect
                        isMulti
                        options={dropdownOptions?.[category?.name] || []} // Options for the dropdown
                        value={
                          attributesState?.[category?.name] && Array.isArray(attributesState?.[category?.name])
                            ? dropdownOptions?.[category?.name]?.filter((opt) =>
                                attributesState?.[category?.name]?.includes(opt?.value),
                              )
                            : []
                        }
                        onChange={(selected) =>
                          handleSelectionChange(category?.name, selected as { value: string; label: string }[])
                        }
                        placeholder={`Select ${(labelMapping as any)[category?.name] || category?.name}`}
                        styles={customStyles}
                        className="react-select-container min-h-[40px] w-full py-4 text-14"
                        classNamePrefix="react-select"
                        isDisabled={dropdownOptions[category?.name] === undefined}
                        menuPortalTarget={document.body}
                        menuPosition="absolute"
                        closeMenuOnSelect={false}
                        blurInputOnSelect={false}
                        backspaceRemovesValue={false}
                        components={{ Option: Option as React.ComponentType<OptionProps<unknown, boolean>> }}
                      />
                    ) : (
                      <select
                        className={`h-[40px] w-full rounded-md border py-3 pl-14 text-start text-14
                        ${attributesState?.[category?.name] ? 'appearance-none border-none bg-[#f5f4f3]' : ''}
                        ${
                          !enabled || dropdownOptions?.[category?.name] === undefined
                            ? 'pointer-events-none text-[#686868]'
                            : ''
                        }`}
                        value={attributesState?.[category?.name] || ''}
                        onChange={(e) => handleSelectionChange(category?.name, e.target.value)}
                        disabled={!enabled || dropdownOptions?.[category?.name] === undefined}
                      >
                        <option value="" disabled>
                          {`Select ${(labelMapping as any)[category?.name] || category?.name}`}
                        </option>
                        {dropdownOptions?.[category?.name]?.map((option, idx) => (
                          <option key={idx} value={option?.value}>
                            {['Shaft', 'Grip'].includes(category?.name) && option?.price > 0
                              ? `${option?.label} (+ ${currency === 'GBP' ? '£' : '€'}${option?.price?.toFixed(2)})`
                              : option?.label}
                          </option>
                        ))}
                      </select>
                    )}

                    {attributesState?.[category?.name] && attributesState?.[category?.name]?.length > 0 ? (
                      <div className="flex h-24 w-24 items-center justify-center rounded-full bg-[#69e8ac] p-2 text-10 font-extrabold text-black">
                        <CheckIcon />
                      </div>
                    ) : (
                      <div className="h-24 w-24 rounded-full border border-[#E7E7E7]"></div>
                    )}
                  </div>
                </div>
              );
            })}
      </div>
      <div className="my-10">
        <p className="font-body text-14 font-normal">
          {'Estimated dispatch date: '}
          <span className="font-normal">
            <span className="font-normal">{deliveryDate || ''}</span>
          </span>
        </p>
      </div>
      <Modal
        shouldCloseOnOverlayClick
        isOpen={modalIsOpen}
        contentLabel={'tooltip'}
        onRequestClose={closeModal}
        preventScroll={true}
        style={{
          content: {
            backgroundColor: '#333',
            width: '580px',
            height: '190px',
          },
        }}
      >
        <div ref={ref}>
          <XMarkIcon
            className="absolute right-15 top-15 h-24 w-24 hover:cursor-pointer"
            strokeWidth={1}
            color="white"
            onClick={closeModal}
          />
          <h3 className="text-lg font-bold text-white">
            {mappedAttribute ? `${mappedAttribute?.charAt(0).toUpperCase()}${mappedAttribute?.slice(1)}` : ''} tooltip
          </h3>
          <div className="mt-4 break-words text-[15px] text-white">{modalContent}</div>
          <div className="mt-6 text-left">
            <button onClick={closeModal} className="cursor-pointer px-4 py-2 text-white underline">
              Close
            </button>
          </div>
        </div>
      </Modal>
    </div>
  );
};

export default CustomFitNew;

export function calculateDeliveryDate(days: number): string {
  if (!days || isNaN(days)) return 'Invalid date'; // Handle invalid or missing days

  const currentDate = new Date(); // Get the current date
  currentDate.setDate(currentDate.getDate() + days); // Add the number of days to the current date

  // Adjust for weekends
  const dayOfWeek = currentDate.getDay(); // 0 = Sunday, 6 = Saturday
  if (dayOfWeek === 6) {
    // If Saturday, add 2 days to make it Monday
    currentDate.setDate(currentDate.getDate() + 2);
  } else if (dayOfWeek === 0) {
    // If Sunday, add 1 day to make it Monday
    currentDate.setDate(currentDate.getDate() + 1);
  }

  // Format the date as "DD/MM/YYYY"
  const day = String(currentDate.getDate()).padStart(2, '0'); // Ensure 2-digit day
  const month = String(currentDate.getMonth() + 1).padStart(2, '0'); // Ensure 2-digit month (Months are 0-based)
  const year = currentDate.getFullYear(); // Get the full year

  return `${day}/${month}/${year}`; // Return the formatted date
}
